export type EntitlementKind = 'billing' | 'provision' | 'feature';

export type EntitlementLimitation = {
  [key: string]: number | undefined;
};

export const EntitlementDefinitions = {
  saml: {
    kind: 'provision',
    systemAdminOnly: true,
  },
  // 監査ログのエクスポート。limitation.startMonth (YYYYMM 数値) = 契約開始月で、
  // エクスポート可能な月の下限になる (listExportableMonths 参照)。
  auditLog: {
    kind: 'feature',
    systemAdminOnly: true,
  },
} as const satisfies Record<
  string,
  {
    kind: EntitlementKind;
    systemAdminOnly: boolean;
  }
>;

export type EntitlementIds = keyof typeof EntitlementDefinitions;
export type EntitlementDef<K extends EntitlementIds> = (typeof EntitlementDefinitions)[K];

export type EntitlementRecord = {
  enabled: boolean;
  limitation?: EntitlementLimitation;
};

export type EntitlementRecords = {
  [K in EntitlementIds]?: EntitlementRecord;
};

export type Entitlements = {
  [K in EntitlementIds]?: EntitlementDef<K> & EntitlementRecord;
};

const ids = Object.keys(EntitlementDefinitions) as EntitlementIds[];

export namespace Entitlements {
  export const Default = (): Entitlements => ({});

  // ループ内の id がユニオン型のままだと result[id] への代入先が never に潰れるため、generic で K を固定する。
  // (optional な mapped type への generic 代入は TS が検証できないため、値の組み立てが正しいことを型で
  //  保証した上で最後だけ cast する)
  const setEntitlement = <K extends EntitlementIds>(result: Entitlements, id: K, input: EntitlementRecord): void => {
    const value: EntitlementDef<K> & EntitlementRecord = {
      ...EntitlementDefinitions[id],
      enabled: input.enabled,
      ...(input.limitation !== undefined ? { limitation: input.limitation } : {}),
    };
    result[id] = value as Entitlements[K];
  };

  export const Construct = (raw?: unknown): Entitlements => {
    if (!raw || typeof raw !== 'object') return Default();
    const record = raw as Record<string, unknown>;
    const result: Entitlements = {};
    for (const id of ids) {
      const value = record[id];
      if (!value || typeof value !== 'object') continue;
      const input = value as EntitlementRecord;
      if (typeof input.enabled !== 'boolean') continue;
      setEntitlement(result, id, input);
    }
    return result;
  };

  export const ToRecords = (entitlements: Entitlements): EntitlementRecords => {
    const records: EntitlementRecords = {};
    for (const id of ids) {
      const value = entitlements[id];
      if (!value) continue;
      records[id] = {
        enabled: value.enabled,
        ...(value.limitation !== undefined ? { limitation: value.limitation } : {}),
      };
    }
    return records;
  };

  export const MergeRecordsUpdate = (cur: Entitlements, patch: EntitlementRecords): Entitlements =>
    Construct({ ...ToRecords(cur), ...patch });

  export const IsEnabled = (entitlements: Entitlements, key: EntitlementIds): boolean => entitlements[key]?.enabled === true;

  export const IsSamlEnabled = (entitlements: Entitlements): boolean => IsEnabled(entitlements, 'saml');

  export const IsAuditLogEnabled = (entitlements: Entitlements): boolean => IsEnabled(entitlements, 'auditLog');

  /** 監査ログの契約開始月 (YYYYMM 数値)。未設定なら undefined */
  export const AuditLogStartMonth = (entitlements: Entitlements): number | undefined => entitlements.auditLog?.limitation?.startMonth;
}
